import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { WarningOctagon, CheckCircle, ArrowUUpLeft, Sparkle, Robot, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminCreditors() {
    const { user } = useAuth();
    const [feedback, setFeedback] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(null);
    const [autofix, setAutofix] = useState(null);
    const [autofixLog, setAutofixLog] = useState([]);
    const [autofixRunning, setAutofixRunning] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [fb, ov, dry, log] = await Promise.all([
                api.get("/creditors/feedback"),
                api.get("/creditors/directory/overrides"),
                api.get("/creditors/auto-fix/dry-run"),
                api.get("/creditors/auto-fix/log"),
            ]);
            setFeedback(fb.data.feedback || []);
            setOverrides(ov.data.overrides || []);
            setAutofix(dry.data);
            setAutofixLog(log.data.runs || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "business") load();
    }, [user]);

    if (user && user.role !== "business") {
        return <Navigate to="/dashboard" replace />;
    }

    const activeByName = Object.fromEntries(overrides.map((o) => [o.name, o]));

    const applyFix = async (row) => {
        const suggested = (row.suggested_emails || []).filter(Boolean);
        if (suggested.length === 0) {
            const manual = window.prompt(
                `No user-suggested replacement email for ${row.name}. Enter the new dispute email manually:`,
                "",
            );
            if (!manual) return;
            await doApply(row.name, manual.trim());
            return;
        }
        const chosen = suggested.length === 1
            ? suggested[0]
            : window.prompt(
                `Multiple suggestions for ${row.name}:\n\n${suggested.map((s, i) => `${i + 1}. ${s}`).join("\n")}\n\nEnter the number to apply (or paste a different email):`,
                suggested[0],
            );
        if (!chosen) return;
        const emailToApply = /^\d+$/.test(chosen)
            ? suggested[parseInt(chosen, 10) - 1]
            : chosen.trim();
        if (!emailToApply) return;
        await doApply(row.name, emailToApply);
    };

    const doApply = async (name, email) => {
        setApplying(name);
        try {
            const { data } = await api.post("/creditors/directory/apply", { name, email });
            toast.success(`Applied override: ${data.old_email} → ${data.new_email} (cleared ${data.reports_cleared} report${data.reports_cleared === 1 ? "" : "s"})`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || formatApiError(e));
        } finally {
            setApplying(null);
        }
    };

    const revert = async (name) => {
        if (!window.confirm(`Revert override for ${name}? The static seed email will be used again.`)) return;
        setApplying(name);
        try {
            await api.delete(`/creditors/directory/apply?name=${encodeURIComponent(name)}`);
            toast.success(`Reverted override for ${name}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || formatApiError(e));
        } finally {
            setApplying(null);
        }
    };

    const runAutofix = async () => {
        setAutofixRunning(true);
        try {
            const { data } = await api.post("/creditors/auto-fix/run");
            if (data.applied_count > 0) {
                toast.success(`Auto-fix applied ${data.applied_count} override${data.applied_count === 1 ? "" : "s"}.`);
            } else {
                toast.info(`Auto-fix ran — no candidates cleared thresholds.`);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || formatApiError(e));
        } finally {
            setAutofixRunning(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="admin-creditors-page">
            <div>
                <p className="overline">Directory admin</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight font-bold">Creditor directory feedback</h1>
                <p className="text-slate-600 mt-3 max-w-2xl">
                    Users report bounced or wrong dispute emails inline from the case-creation flow.
                    Review the aggregated complaints below and apply the most-suggested replacement — the
                    change goes live to every user immediately without a code deploy.
                </p>
            </div>

            {/* ---------- Auto-fix cron card ---------- */}
            <Card className="p-6 border-2 border-[#0F1E3D] bg-gradient-to-br from-slate-50 to-white" data-testid="autofix-card">
                <div className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-full bg-[#0F1E3D] text-white flex items-center justify-center shrink-0">
                        <Robot size={22} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <h2 className="font-serif text-2xl font-bold text-[#0F1E3D]">Nightly auto-fix cron</h2>
                                <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
                                    Every day at <span className="font-mono">09:00 UTC</span> the directory self-heals: any entry with
                                    <span className="font-mono"> ≥{autofix?.min_reports ?? "…"}</span> reports whose oldest
                                    complaint is <span className="font-mono">≥{autofix?.min_age_hours ?? "…"}h</span> old
                                    is overridden with the majority-voted user suggestion. You can still revert anything from the table below.
                                </p>
                            </div>
                            <Button
                                onClick={runAutofix}
                                disabled={autofixRunning}
                                className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10"
                                data-testid="autofix-run-btn"
                            >
                                <Play size={14} className="mr-1.5" weight="fill" />
                                {autofixRunning ? "Running…" : "Run auto-fix now"}
                            </Button>
                        </div>

                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <MiniStat label="Pending candidates" value={autofix?.candidate_count ?? "—"} tint="amber" testId="autofix-pending" />
                            <MiniStat label="Active overrides" value={overrides.length} tint="emerald" />
                            <MiniStat label="Min reports" value={autofix?.min_reports ?? "—"} tint="slate" />
                            <MiniStat label="Age window (h)" value={autofix?.min_age_hours ?? "—"} tint="slate" />
                        </div>

                        {autofix?.candidates?.length > 0 && (
                            <div className="mt-4 border border-slate-200 rounded-md overflow-hidden" data-testid="autofix-pending-list">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Entry</th>
                                            <th className="text-left px-3 py-2 font-medium">Current → Auto-fix</th>
                                            <th className="text-right px-3 py-2 font-medium">Reports</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {autofix.candidates.map((c) => (
                                            <tr key={c.name} className="border-t border-slate-200">
                                                <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                                                <td className="px-3 py-2 font-mono text-slate-600">
                                                    <span className="line-through text-slate-400">{c.current_email}</span>{" "}
                                                    → <span className="text-emerald-700">{c.suggested_email}</span>
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono">{c.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {autofixLog.length > 0 && (
                            <div className="mt-5 pt-4 border-t border-slate-200" data-testid="autofix-log">
                                <p className="overline mb-2">Recent runs</p>
                                <ul className="space-y-1.5 text-xs text-slate-600">
                                    {autofixLog.slice(0, 5).map((r) => (
                                        <li key={r.id} className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-slate-500">{new Date(r.at).toLocaleString()}</span>
                                            <span className="text-slate-500">·</span>
                                            <span>{r.triggered_by.startsWith("manual") ? "manual" : "cron"}</span>
                                            <span className="text-slate-500">·</span>
                                            <span className={r.applied_count > 0 ? "text-emerald-700 font-medium" : "text-slate-500"}>
                                                {r.applied_count} applied
                                            </span>
                                            {r.failed_count > 0 && <span className="text-rose-700">· {r.failed_count} failed</span>}
                                            <span className="text-slate-400">· {r.candidates_seen} candidates seen</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* ---------- Active overrides ---------- */}
            <Card className="p-6" data-testid="active-overrides-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-serif text-2xl font-bold text-[#0F1E3D]">Active overrides</h2>
                        <p className="text-xs text-slate-500 mt-1">Currently swapping the seed dispute email at lookup time.</p>
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">{overrides.length}</Badge>
                </div>
                {loading ? (
                    <p className="text-sm text-slate-500 mt-4">Loading…</p>
                ) : overrides.length === 0 ? (
                    <p className="text-sm text-slate-500 mt-4 italic">No overrides applied yet.</p>
                ) : (
                    <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm" data-testid="active-overrides-table">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Directory entry</th>
                                    <th className="text-left px-3 py-2 font-medium">Original email</th>
                                    <th className="text-left px-3 py-2 font-medium">Active override</th>
                                    <th className="text-right px-3 py-2 font-medium">Applied</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {overrides.map((o) => (
                                    <tr key={o.name} className="border-t border-slate-200" data-testid={`override-row-${o.name}`}>
                                        <td className="px-3 py-2 font-medium text-slate-900">{o.name}</td>
                                        <td className="px-3 py-2 text-slate-500 font-mono text-xs line-through">{o.original_email}</td>
                                        <td className="px-3 py-2 text-emerald-800 font-mono text-xs">{o.email}</td>
                                        <td className="px-3 py-2 text-right text-slate-500 text-xs">{new Date(o.applied_at).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => revert(o.name)}
                                                disabled={applying === o.name}
                                                className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                                data-testid={`revert-${o.name}`}
                                            >
                                                <ArrowUUpLeft size={13} className="mr-1.5" />
                                                Revert
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* ---------- Feedback queue ---------- */}
            <Card className="p-6" data-testid="feedback-queue-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-serif text-2xl font-bold text-[#0F1E3D]">Wrong-email reports</h2>
                        <p className="text-xs text-slate-500 mt-1">Sorted by count desc. Click Auto-fix to apply the top user-suggested replacement.</p>
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">{feedback.length}</Badge>
                </div>
                {loading ? (
                    <p className="text-sm text-slate-500 mt-4">Loading…</p>
                ) : feedback.length === 0 ? (
                    <p className="text-sm text-slate-500 mt-4 italic">No wrong-email reports yet. Users will report bounced or outdated dispute emails from the New-Case creditor autocomplete.</p>
                ) : (
                    <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm" data-testid="feedback-table">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Directory entry</th>
                                    <th className="text-left px-3 py-2 font-medium">Reported email</th>
                                    <th className="text-left px-3 py-2 font-medium">Reasons</th>
                                    <th className="text-right px-3 py-2 font-medium">Reports</th>
                                    <th className="text-left px-3 py-2 font-medium">Top user suggestion</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {feedback.map((row) => {
                                    const overrideActive = activeByName[row.name];
                                    const topSuggested = (row.suggested_emails || []).filter(Boolean)[0] || null;
                                    return (
                                        <tr key={`${row.name}::${row.email}`} className="border-t border-slate-200" data-testid={`feedback-row-${row.name}`}>
                                            <td className="px-3 py-2">
                                                <div className="font-medium text-slate-900">{row.name}</div>
                                                {overrideActive && (
                                                    <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200">
                                                        <CheckCircle size={9} weight="fill" /> already fixed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.email}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {row.reasons.map((r) => (
                                                        <Badge key={r} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                                                            <WarningOctagon size={9} weight="fill" className="mr-1" />
                                                            {r.replace(/_/g, " ")}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">{row.count}</td>
                                            <td className="px-3 py-2 font-mono text-xs text-emerald-700">{topSuggested || "—"}</td>
                                            <td className="px-3 py-2 text-right">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => applyFix(row)}
                                                    disabled={applying === row.name}
                                                    className="h-8"
                                                    data-testid={`apply-${row.name}`}
                                                >
                                                    <Sparkle size={13} className="mr-1.5" weight="fill" />
                                                    {applying === row.name ? "Applying…" : "Auto-fix"}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <p className="text-xs text-slate-500 max-w-2xl">
                Overrides live in <span className="font-mono">db.creditor_directory_overrides</span> and are applied at
                lookup time — the static seed list in <span className="font-mono">data/creditor_directory.py</span> is
                never modified. Revert an override to fall back to the seed value.
            </p>
        </div>
    );
}

function MiniStat({ label, value, tint, testId }) {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        slate: "bg-slate-50 text-slate-800 border-slate-200",
    };
    return (
        <div className={`p-2.5 rounded-md border ${tones[tint] || tones.slate}`} data-testid={testId}>
            <p className="overline text-[10px]">{label}</p>
            <p className="font-mono text-base font-semibold mt-0.5">{value}</p>
        </div>
    );
}
